'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { usePathname } from 'next/navigation';

interface SessionDebugState {
  timestamp: string;
  authContextUser: any;
  authContextSession: any;
  authContextLoading: boolean;
  cookiePresent: boolean;
  cookieDetails: string | null;
  tokenExpiration: string | null;
  tokenTimeRemaining: string | null;
  rlsPolicyStatus: 'testing' | 'passed' | 'failed' | 'not-tested';
  rlsPolicyMessage: string;
  currentPath: string;
  navigationHistory: string[];
}

export function SessionDebugPanel() {
  const { user, session, loading } = useAuth();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [debugState, setDebugState] = useState<SessionDebugState>({
    timestamp: new Date().toISOString(),
    authContextUser: null,
    authContextSession: null,
    authContextLoading: true,
    cookiePresent: false,
    cookieDetails: null,
    tokenExpiration: null,
    tokenTimeRemaining: null,
    rlsPolicyStatus: 'not-tested',
    rlsPolicyMessage: 'Not tested yet',
    currentPath: pathname,
    navigationHistory: [pathname],
  });

  // Check for Supabase auth cookies
  const checkCookies = () => {
    if (typeof document === 'undefined') return { present: false, details: null };

    const cookies = document.cookie.split(';');
    const supabaseCookie = cookies.find(cookie => 
      cookie.trim().startsWith('sb-') && cookie.includes('-auth-token')
    );

    return {
      present: !!supabaseCookie,
      details: supabaseCookie?.trim() || null,
    };
  };

  // Calculate token expiration details
  const getTokenExpiration = () => {
    if (!session?.expires_at) return { expiration: null, remaining: null };

    const expiresAt = new Date(session.expires_at * 1000);
    const now = new Date();
    const remainingMs = expiresAt.getTime() - now.getTime();
    const remainingMinutes = Math.floor(remainingMs / 60000);
    const remainingSeconds = Math.floor((remainingMs % 60000) / 1000);

    return {
      expiration: expiresAt.toLocaleString(),
      remaining: remainingMs > 0 
        ? `${remainingMinutes}m ${remainingSeconds}s` 
        : 'Expired',
    };
  };

  // Test RLS policies by attempting to read user_profiles
  const testRLSPolicies = async () => {
    if (!user?.id) {
      setDebugState(prev => ({
        ...prev,
        rlsPolicyStatus: 'not-tested',
        rlsPolicyMessage: 'No user session available',
      }));
      return;
    }

    setDebugState(prev => ({
      ...prev,
      rlsPolicyStatus: 'testing',
      rlsPolicyMessage: 'Testing RLS policies...',
    }));

    try {
      const supabase = createClient();
      
      // Test reading own profile (should always work)
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, email, role')
        .eq('id', user.id)
        .single();

      if (profileError) {
        setDebugState(prev => ({
          ...prev,
          rlsPolicyStatus: 'failed',
          rlsPolicyMessage: `RLS FAILED: ${profileError.message}`,
        }));
        return;
      }

      // Test reading articles (role-dependent)
      const { data: articlesData, error: articlesError } = await supabase
        .from('articles')
        .select('id, title, author_id')
        .limit(1);

      if (articlesError && articlesError.code !== 'PGRST116') {
        setDebugState(prev => ({
          ...prev,
          rlsPolicyStatus: 'failed',
          rlsPolicyMessage: `RLS PARTIALLY FAILED: Profile ✓, Articles ✗ (${articlesError.message})`,
        }));
        return;
      }

      setDebugState(prev => ({
        ...prev,
        rlsPolicyStatus: 'passed',
        rlsPolicyMessage: `RLS PASSED: Profile ✓, Articles ${articlesError ? '✗' : '✓'}`,
      }));
    } catch (error: any) {
      setDebugState(prev => ({
        ...prev,
        rlsPolicyStatus: 'failed',
        rlsPolicyMessage: `RLS TEST ERROR: ${error.message}`,
      }));
    }
  };

  // Update debug state whenever auth context changes
  useEffect(() => {
    const cookieInfo = checkCookies();
    const tokenInfo = getTokenExpiration();

    setDebugState(prev => ({
      ...prev,
      timestamp: new Date().toISOString(),
      authContextUser: user,
      authContextSession: session,
      authContextLoading: loading,
      cookiePresent: cookieInfo.present,
      cookieDetails: cookieInfo.details,
      tokenExpiration: tokenInfo.expiration,
      tokenTimeRemaining: tokenInfo.remaining,
    }));

    // Auto-test RLS when user becomes available
    if (user && !loading) {
      testRLSPolicies();
    }
  }, [user, session, loading]);

  // Track navigation
  useEffect(() => {
    setDebugState(prev => {
      const newHistory = [...prev.navigationHistory];
      if (newHistory[newHistory.length - 1] !== pathname) {
        newHistory.push(pathname);
        if (newHistory.length > 10) newHistory.shift(); // Keep last 10
      }
      return {
        ...prev,
        currentPath: pathname,
        navigationHistory: newHistory,
      };
    });
  }, [pathname]);

  // Auto-refresh token info every second
  useEffect(() => {
    const interval = setInterval(() => {
      if (session?.expires_at) {
        const tokenInfo = getTokenExpiration();
        setDebugState(prev => ({
          ...prev,
          tokenExpiration: tokenInfo.expiration,
          tokenTimeRemaining: tokenInfo.remaining,
        }));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [session]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'passed': return 'text-green-400';
      case 'failed': return 'text-red-400';
      case 'testing': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBadge = (condition: boolean, trueText: string, falseText: string) => {
    return condition ? (
      <span className="px-2 py-0.5 text-xs font-semibold bg-green-500/20 text-green-400 rounded">
        {trueText}
      </span>
    ) : (
      <span className="px-2 py-0.5 text-xs font-semibold bg-red-500/20 text-red-400 rounded">
        {falseText}
      </span>
    );
  };

  return (
    <>
      {/* Toggle Button - Fixed bottom-right */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 z-[9998] px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-lg font-medium text-sm transition-colors"
        aria-label="Toggle Session Debug Panel"
      >
        {isOpen ? '✕ Close Debug' : '🔍 Session Debug'}
      </button>

      {/* Debug Panel - Floating overlay */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 z-[9999] w-96 max-h-[80vh] overflow-y-auto bg-gray-900 border border-gray-700 rounded-lg shadow-2xl">
          <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
            <h3 className="text-white font-semibold text-sm">🔐 Session State Monitor</h3>
            <button
              onClick={testRLSPolicies}
              className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
            >
              Test RLS
            </button>
          </div>

          <div className="p-4 space-y-4 text-xs">
            {/* Authentication Status */}
            <div className="bg-gray-800 rounded p-3 space-y-2">
              <h4 className="text-gray-300 font-semibold flex items-center justify-between">
                Authentication Status
                {getStatusBadge(!!user, 'AUTHENTICATED', 'NOT AUTHENTICATED')}
              </h4>
              <div className="space-y-1 text-gray-400">
                <div className="flex justify-between">
                  <span>Loading:</span>
                  <span className="font-mono">{loading ? 'true' : 'false'}</span>
                </div>
                <div className="flex justify-between">
                  <span>User Email:</span>
                  <span className="font-mono">{user?.email || 'None'}</span>
                </div>
                <div className="flex justify-between">
                  <span>User Role:</span>
                  <span className="font-mono">{user?.role || 'None'}</span>
                </div>
                <div className="flex justify-between">
                  <span>User ID:</span>
                  <span className="font-mono text-[10px]">{user?.id || 'None'}</span>
                </div>
              </div>
            </div>

            {/* Cookie Status */}
            <div className="bg-gray-800 rounded p-3 space-y-2">
              <h4 className="text-gray-300 font-semibold flex items-center justify-between">
                Browser Cookie
                {getStatusBadge(debugState.cookiePresent, 'PRESENT', 'MISSING')}
              </h4>
              {debugState.cookieDetails && (
                <div className="text-gray-400 text-[10px] font-mono break-all">
                  {debugState.cookieDetails.substring(0, 50)}...
                </div>
              )}
            </div>

            {/* Token Status */}
            <div className="bg-gray-800 rounded p-3 space-y-2">
              <h4 className="text-gray-300 font-semibold">Session Token</h4>
              <div className="space-y-1 text-gray-400">
                <div className="flex justify-between">
                  <span>Expires At:</span>
                  <span className="font-mono text-[10px]">{debugState.tokenExpiration || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Time Remaining:</span>
                  <span className={`font-mono font-semibold ${
                    debugState.tokenTimeRemaining?.includes('Expired') ? 'text-red-400' : 'text-green-400'
                  }`}>
                    {debugState.tokenTimeRemaining || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* RLS Policy Status */}
            <div className="bg-gray-800 rounded p-3 space-y-2">
              <h4 className="text-gray-300 font-semibold flex items-center justify-between">
                RLS Policy Validation
                <span className={`text-xs font-semibold ${getStatusColor(debugState.rlsPolicyStatus)}`}>
                  {debugState.rlsPolicyStatus.toUpperCase()}
                </span>
              </h4>
              <div className={`text-[11px] ${getStatusColor(debugState.rlsPolicyStatus)}`}>
                {debugState.rlsPolicyMessage}
              </div>
            </div>

            {/* Navigation History */}
            <div className="bg-gray-800 rounded p-3 space-y-2">
              <h4 className="text-gray-300 font-semibold">Navigation History</h4>
              <div className="space-y-1">
                <div className="flex justify-between text-gray-400">
                  <span>Current:</span>
                  <span className="font-mono text-blue-400">{debugState.currentPath}</span>
                </div>
                <div className="text-gray-500 text-[10px] space-y-0.5">
                  {debugState.navigationHistory.slice(-5).map((path, idx) => (
                    <div key={idx} className="font-mono">
                      {idx + 1}. {path}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Session Object (collapsed) */}
            <details className="bg-gray-800 rounded p-3">
              <summary className="text-gray-300 font-semibold cursor-pointer">
                Raw Session Object
              </summary>
              <pre className="mt-2 text-[9px] text-gray-400 overflow-x-auto">
                {JSON.stringify(session, null, 2)}
              </pre>
            </details>

            {/* Timestamp */}
            <div className="text-center text-gray-500 text-[10px] font-mono">
              Last Updated: {new Date(debugState.timestamp).toLocaleTimeString()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}